import styled from '@emotion/styled'
import React, { useCallback, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { colors } from '@/styles/theme'
import { ColorSelector } from '@/components/atoms/ColorSelector'
import { PlatformSelector } from '@/components/atoms/PlatformSelector'
import { SexSelector } from '@/components/atoms/SexSelector'
import { VoiceChatSelector } from '@/components/atoms/VoiceChatSelector'
import {
  InputText,
  InputSelector,
  OptionsPlaytime,
} from '@/components/atoms/Inputs'
import { VoiceChat } from '@/firebase/firestore/VoiceChats'
import { Platform } from '@/firebase/firestore/Platform'
import { Sex } from '@/firebase/firestore/Sex'
import { ImageSelector } from '@/components/atoms/ImageSelector'
import { useLocale } from '@/hooks/useLocale'
import { AmongUs1 } from './AmongUs1'
import { AmongUs2 } from './AmongUs2'
import { AmongUs3 } from './AmongUs3'
import { AmongUsSelector } from './AmongUsSelector'
import {
  CREATOR,
  creatorMapTargetId,
  CREATORS,
  mapAmoungUsCreatorInfo,
} from './config'
import { format } from 'date-fns'
import { ProfileCardEditorDescription } from '@/components/atoms/ProfileCardEditorDescription'
import { useSearchParams } from 'next/navigation'

type FormField = {
  id: string
  name: string
  iconImage: string
  playTimeWeekDayBegin: string
  playTimeWeekDayEnd: string
  playTimeWeekEndBegin: string
  playTimeWeekEndEnd: string
  playStyle: string
  hitokoto: string
}

type Props = {
  onCreate: (
    data: FormField & { favoriteClue: number[] },
    targetId: string,
    selectedSex: Sex,
    selectedPlatform: Array<Platform>,
    selectedVoiceChat: Array<VoiceChat>
  ) => void
  submitButtonDisable: boolean
  creatorParam: string | null
}

const FONT_FAMILIES = [
  {
    imagePath: '/static/images/fonts/ap.png',
    value: 'ap',
  },
  {
    imagePath: '/static/images/fonts/anko.png',
    value: 'anko',
  },
  {
    imagePath: '/static/images/fonts/smart_ui.png',
    value: 'smartfont',
  },
  {
    imagePath: '/static/images/fonts/noto_sans.png',
    value: 'notosans',
  },
] as const

const FONT_COLORS = [
  '#FD8080',
  '#FD7C24',
  '#29DDF2',
  '#C05AE0',
  '#7AA852',
  '#4C4C4C',
]

const creatorMapCharacters: Record<CREATOR, string[]> = {
  pira: [],
  dotcom: [],
  __nyan299: Array(12)
    .fill(0)
    .map((_, index) => {
      return `https://storage.googleapis.com/nadare-production-static-images/static/images/profileCards/amongus/amongus3/characters/${index}.png`
    }),
}

type FONT_FAMILY = (typeof FONT_FAMILIES)[number]['value']

const creators = Object.keys(mapAmoungUsCreatorInfo).map((creatorName) => {
  return {
    imagePath: mapAmoungUsCreatorInfo[creatorName]['creatorImageURL'],
    value: creatorName,
  }
})

export const EditorAmongUs: React.FC<Props> = ({
  onCreate,
  submitButtonDisable,
  creatorParam,
}) => {
  const { t } = useLocale()
  const { handleSubmit, register, watch } = useForm<FormField>({
    defaultValues: {
      playTimeWeekDayBegin: '18',
      playTimeWeekDayEnd: '19',
      playTimeWeekEndBegin: '18',
      playTimeWeekEndEnd: '19',
    },
  })
  const fieldVal = watch()

  const [selectedCreator, setSelectedCreator] = useState<CREATOR>(
    CREATORS.includes(creatorParam as any)
      ? (creatorParam as CREATOR)
      : CREATORS[0]
  )
  useEffect(() => {
    if (!selectedCreator) return
    const url = new URL(window.location.href)
    url.searchParams.set('creator', selectedCreator)
    window.history.pushState(null, '', url.toString())
  }, [selectedCreator])

  const [selectedFontFamily, setSelectedFontFamily] =
    useState<FONT_FAMILY>('ap')
  const [selectedFontColor, setFontColor] = useState(0)
  const onChangeFontColor = useCallback((selectedIndex: number) => {
    setFontColor(selectedIndex)
  }, [])
  const [selectedSex, setSex] = useState<Sex>(0)
  const onChangeSex = useCallback((selectedIndex: Sex) => {
    setSex(selectedIndex)
  }, [])

  const [selectedPlatform, setPlatform] = useState<Array<0 | 3 | 4>>([0])
  const onChangePlatform = useCallback((selectedIndex: Array<0 | 3 | 4>) => {
    setPlatform(selectedIndex)
  }, [])

  const [selectedVoiceChat, setVoiceChat] = useState<
    Array<0 | 1 | 3 | 4 | 100>
  >([])
  const onChangeVoiceChat = useCallback(
    (selectedIndex: Array<0 | 1 | 3 | 4 | 100>) => {
      setVoiceChat(selectedIndex)
    },
    []
  )
  const [selectedCharacter, setCharacter] = useState<Array<number>>([])
  const onChangeCharacter = useCallback((selectedIndex: number[]) => {
    setCharacter(selectedIndex)
  }, [])

  const handleCreate = (data: FormField) => {
    onCreate(
      { ...data, favoriteClue: selectedCharacter },
      creatorMapTargetId[selectedCreator],
      selectedSex,
      selectedPlatform,
      selectedVoiceChat
    )
  }

  const [preview, setPreview] = useState<string | null>(null)

  const handleImageUpload = (e) => {
    const file = e.target.files[0]
    if (file) {
      const reader = new FileReader()
      reader.onloadend = () => {
        if (typeof reader.result === 'string') {
          setPreview(reader.result)
        }
      }
      reader.readAsDataURL(file)
    }
  }

  const lastModText = format(new Date(), 'yyyy年MM月dd日')
  return (
    <>
      <PreviewContainer>
        <PreviewCardContainer>
          {selectedCreator === 'pira' && (
            <AmongUs1
              previewImage={preview ?? ''}
              targetId={creatorMapTargetId[selectedCreator]}
              fontFamily={selectedFontFamily}
              fontColor={FONT_COLORS[selectedFontColor]}
              name={fieldVal.name}
              sex={selectedSex}
              platform={selectedPlatform}
              voiceChat={selectedVoiceChat}
              playStyle={fieldVal.playStyle}
              playTimeWeekDayBegin={fieldVal.playTimeWeekDayBegin}
              playTimeWeekDayEnd={fieldVal.playTimeWeekDayEnd}
              playTimeWeekEndBegin={fieldVal.playTimeWeekEndBegin}
              playTimeWeekEndEnd={fieldVal.playTimeWeekEndEnd}
              hitokoto={fieldVal.hitokoto}
            />
          )}
          {selectedCreator === 'dotcom' && (
            <AmongUs2
              targetId={creatorMapTargetId[selectedCreator]}
              fontFamily={selectedFontFamily}
              fontColor={FONT_COLORS[selectedFontColor]}
              uid={fieldVal.id}
              name={fieldVal.name}
              sex={selectedSex}
              platform={selectedPlatform}
              voiceChat={selectedVoiceChat}
              playStyle={fieldVal.playStyle}
              playTimeWeekDayBegin={fieldVal.playTimeWeekDayBegin}
              playTimeWeekDayEnd={fieldVal.playTimeWeekDayEnd}
              playTimeWeekEndBegin={fieldVal.playTimeWeekEndBegin}
              playTimeWeekEndEnd={fieldVal.playTimeWeekEndEnd}
              hitokoto={fieldVal.hitokoto}
            />
          )}
          {selectedCreator === '__nyan299' && (
            <AmongUs3
              targetId={creatorMapTargetId[selectedCreator]}
              fontFamily={selectedFontFamily}
              fontColor={FONT_COLORS[selectedFontColor]}
              uid={fieldVal.id}
              name={fieldVal.name}
              sex={selectedSex}
              platform={selectedPlatform}
              voiceChat={selectedVoiceChat}
              playStyle={fieldVal.playStyle}
              playTimeWeekDayBegin={fieldVal.playTimeWeekDayBegin}
              playTimeWeekDayEnd={fieldVal.playTimeWeekDayEnd}
              playTimeWeekEndBegin={fieldVal.playTimeWeekEndBegin}
              playTimeWeekEndEnd={fieldVal.playTimeWeekEndEnd}
              hitokoto={fieldVal.hitokoto}
              favoriteCrews={selectedCharacter}
            />
          )}
        </PreviewCardContainer>
      </PreviewContainer>
      <DescriptionSection>
        <Time>
          <time dateTime={new Date().toISOString()} itemProp="datepublished" />
          <time dateTime={new Date().toISOString()} itemProp="modified">
            {t.最終更新}: {lastModText}
          </time>
        </Time>
        <H1Text>{t.AmongUsかわいい自己紹介カード作成ツール}</H1Text>
        <H2Text>{t.AmongUsかわいい自己紹介カード作成ツール説明文}</H2Text>
        <ProfileCardEditorDescription />
      </DescriptionSection>
      <FormContainer>
        <form
          onKeyDown={(e) => e.key === 'Enter' && e.preventDefault()}
          onSubmit={handleSubmit(handleCreate)}
        >
          <FormItemContainer>
            <VerticalContainer>
              <SectionText>{t.テンプレートを選ぶ}</SectionText>
              <ImageSelector
                values={creators}
                onChange={setSelectedCreator as any}
                value={selectedCreator}
              />
            </VerticalContainer>
          </FormItemContainer>
          <FormItemContainer>
            <SectionText>{t.文字色を選ぶ}</SectionText>
            <ColorSelector
              colors={FONT_COLORS}
              value={selectedFontColor}
              onChange={onChangeFontColor}
            />
          </FormItemContainer>
          <FormItemContainer>
            <SectionText>{t.フォントを選ぶ}</SectionText>
            <ImageSelector
              values={FONT_FAMILIES}
              onChange={setSelectedFontFamily as any}
              value={selectedFontFamily}
            />
          </FormItemContainer>
          {selectedCreator === 'pira' && (
            <FormItemContainer>
              <Label>{t.アイコン画像}</Label>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
              />
            </FormItemContainer>
          )}
          <FormItemContainer>
            <Label>{t.なまえ}</Label>
            <InputText {...register('name', { maxLength: 20 })} />
          </FormItemContainer>
          {selectedCreator === 'dotcom' && (
            <FormItemContainer>
              <Label>{t.ID}</Label>
              <InputText {...register('id', { maxLength: 20 })} />
            </FormItemContainer>
          )}
          <FormItemContainer>
            <Label>{t.性別}</Label>
            <SexSelector val={selectedSex} onChange={onChangeSex} />
          </FormItemContainer>
          <FormItemContainer>
            <Label>{t.プラットフォーム}</Label>
            <PlatformSelector
              activeValues={
                selectedCreator === '__nyan299' ? [0, 4, 3] : [4, 0, 3]
              }
              onChange={onChangePlatform}
              value={selectedPlatform}
            />
          </FormItemContainer>
          {selectedCreator === 'pira' && (
            <FormItemContainer>
              <Label>{t.プレイスタイル}</Label>
              <InputSelector {...register('playStyle')}>
                <option value="">未選択</option>
                <option value="beginner">初心者</option>
                <option value="serious">ガチ</option>
                <option value="enjoy">エンジョイ</option>
              </InputSelector>
            </FormItemContainer>
          )}
          {selectedCreator === '__nyan299' && (
            <FormItemContainer>
              <Label>{t.プレイスタイル}</Label>
              <InputSelector {...register('playStyle')}>
                <option value="">未選択</option>
                <option value="beginner">初心者</option>
                <option value="serious">ガチ</option>
                <option value="enjoy">エンジョイ</option>
              </InputSelector>
            </FormItemContainer>
          )}
          <FormItemContainer>
            <Label>{t.ボイスチャット}</Label>
            <VoiceChatSelector
              onChange={onChangeVoiceChat}
              value={selectedVoiceChat}
              activeValue={
                selectedCreator == 'pira'
                  ? [1, 3, 0, 100, 4]
                  : selectedCreator == 'dotcom'
                  ? [1, 3, 4]
                  : selectedCreator == '__nyan299'
                  ? [3, 1, 0, 4]
                  : [1, 3, 4]
              }
            />
          </FormItemContainer>
          {selectedCreator === '__nyan299' && (
            <FormItemContainer>
              <Label>{t.好きなクルーの色}</Label>
              <AmongUsSelector
                values={creatorMapCharacters[selectedCreator]}
                onChange={onChangeCharacter}
              />
            </FormItemContainer>
          )}
          <FormItemContainer>
            <Label>{t.プレイ時間帯平日}</Label>
            <InputSelector {...register('playTimeWeekDayBegin')}>
              <OptionsPlaytime />
            </InputSelector>
            <TimeSelectorText>から</TimeSelectorText>
            <InputSelector {...register('playTimeWeekDayEnd')}>
              <OptionsPlaytime />
            </InputSelector>
          </FormItemContainer>
          <FormItemContainer>
            <Label>{t.プレイ時間帯土日}</Label>
            <InputSelector {...register('playTimeWeekEndBegin')}>
              <OptionsPlaytime />
            </InputSelector>
            <TimeSelectorText>{t.から}</TimeSelectorText>
            <InputSelector {...register('playTimeWeekEndEnd')}>
              <OptionsPlaytime />
            </InputSelector>
          </FormItemContainer>
          {selectedCreator === 'dotcom' && (
            <FormItemContainer>
              <Label>{t.プレイスタイル}</Label>
              <InputText {...register('playStyle', { maxLength: 50 })} />
            </FormItemContainer>
          )}
          <FormItemContainer>
            <Label>{t.ひとこと}</Label>
            <InputText {...register('hitokoto', { maxLength: 50 })} />
          </FormItemContainer>
          <ButtonDescriptionText>
            {t.カードの作成にはSNS連携が必要です次の画面でログインをします}
          </ButtonDescriptionText>
          <Submit
            id="create_profile"
            type="submit"
            value={t.これでつくる}
            disabled={submitButtonDisable}
          />
        </form>
      </FormContainer>
    </>
  )
}

const DescriptionSection = styled.label`
  margin-top: 16px;
  margin-left: 16px;
  margin-right: 16px;
`

const Time = styled.div`
  color: ${colors.text.secondary};
  font-size: 12px;
`

const H1Text = styled.h1`
  margin-top: 12px;
  font-size: 11px;
  font-weight: bold;
  line-height: 20px;
  color: ${colors.text.primary};
  text-align: center;
  display: flex;
`

const H2Text = styled.h2`
  margin-top: 20px;
  font-size: 11px;
  color: ${colors.text.secondary};
  line-height: 18px;
  display: flex;
`

const PreviewContainer = styled.div`
  position: sticky;
  top: 0;
  z-index: 1000;
`

const PreviewCardContainer = styled.div`
  background-color: ${colors.bg.primary};
  box-shadow: 0 0 4px rgba(32, 32, 32, 0.1);
`

const FormContainer = styled.div`
  padding: 32px 16px;
`

const FormItemContainer = styled.div`
  margin-bottom: 32px;
`

const Label = styled.label`
  font-weight: bold;
  font-size: 18px;
  color: ${colors.text.primary};
  margin-bottom: 16px;
  display: block;
`

const SectionText = styled.div`
  margin-top: 20px;
  margin-bottom: 16px;
  font-weight: bold;
  font-size: 18px;
  color: ${colors.text.primary};
  display: flex;
`

const TimeSelectorText = styled.div`
  margin: 8px auto;
  color: ${colors.text.primary};
  font-size: 12px;
  font-weight: bold;
`

const ButtonDescriptionText = styled.h3`
  margin: auto;
  margin-top: 30px;
  width: 100%;
  font-size: 10px;
  color: ${colors.text.secondary};
  justify-content: center;
  display: flex;
`

const Submit = styled.input`
  margin-top: 12px;
  border-radius: 20px;
  border: 3px solid transparent;
  width: 100%;
  height: 40px;
  background-color: ${colors.button.primary};
  color: #fff;
  font-weight: bold;
  display: block;
`

const VerticalContainer = styled.div`
  display: flex;
  flex-flow: column;
`
